import { memo } from "react";
import styled from "styled-components";
import {
  ArrowTrendingUpIcon,
  DocumentMinusIcon,
  Square2StackIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { tags } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Card as Base } from "~/client/components";
import { Group as Extension } from "~/client/extensions";
import {
  useAdditionalFieldsPurpose,
  useStreamActions,
  useTimeFlip,
} from "../../logic";
import Fields from "../fields";

const WrapperPartial = styled(Base)`
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${(props) => props.theme.sizes.formEdge};
  width: 100%;
  padding-top: calc(${(props) => props.theme.sizes.formEdge} * 1);

  & > * {
    grid-column: span 2;
  }
`;

const Extended = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: ${(props) => props.theme.sizes.formEdge};
  width: 100%;
  column-gap: ${(props) => props.theme.sizes.edge};
  & > * {
    grid-column: span 2;
  }

  &:not(:empty) {
    padding-top: calc(${(props) => props.theme.sizes.formEdge} * 1);
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: 4px;
    *[data-disabled="true"] {
      border: 2px solid ${(props) => props.theme.colors.transparent} !important;
      background-color: ${(props) => props.theme.colors.transparent} !important;
    }
  }
`;

const Divider = styled.div`
  width: 2px;
  height: 30px;
  background-color: ${(props) => props.theme.colors.border};
`;

const Moment = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: calc(${(props) => props.theme.sizes.edge} * 1);
`;

const Switch = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    height: ${(props) => props.theme.sizes.inputBox};
    margin-top: 28px;
    padding-inline: calc(${(props) => props.theme.sizes.formEdge} * 1 / 2);
    border-radius: 6px;
    background-color: ${(props) => props.theme.colors.dark100};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxXL} {
    width: 100%;
    ${Grid} {
      grid-template-columns: 1fr;
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
      & > * {
        grid-column: span 1;
      }
    }
  }

  ${(props) => props.theme.medias.maxLG} {
    ${Grid} {
      grid-template-columns: 1fr 1fr;
      width: 100%;
    }
  }
  ${(props) => props.theme.medias.maxMD} {
    & > *[data-component="card"] {
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      border-radius: 0;
    }
    ${Grid} {
      grid-template-columns: 1fr;
    }
    ${Extended} {
      grid-template-columns: 1fr;
    }
  }

  ${(props) => props.theme.medias.maxSM} {
    ${Moment} {
      grid-template-columns: 1fr auto;
      gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      & > *:first-child {
        grid-column: span 2;
      }
    }
    ${Switch} {
      margin-top: 34px;
      padding-inline: calc(${(props) => props.theme.sizes.formEdge} * 1 / 2);
    }
  }
`;
interface ExtraProps {
  streamId: string;
}

function Extra({ streamId }: ExtraProps) {
  const { onDuplicate, onRemove, isRemovable, simulate, isValid } =
    useStreamActions(streamId);
  const { t } = useT();

  return (
    <Actions>
      <Button
        appearance={"transparent"}
        accent={"gray400"}
        isMini
        onClick={simulate}
        isDisabled={!isValid}
        right={ArrowTrendingUpIcon}
        title={t("words.simulation")}
      />
      <Divider />
      {isRemovable && (
        <>
          <Button
            appearance={"transparent"}
            accent={"gray400"}
            isMini
            onClick={onRemove}
            right={DocumentMinusIcon}
            title={t("words.remove")}
            titleShort={""}
          />
          <Divider />
        </>
      )}
      <Button
        appearance={"transparent"}
        accent={"gray400"}
        isMini
        onClick={onDuplicate}
        right={Square2StackIcon}
        title={t("words.duplicate")}
        titleShort={""}
      />
    </Actions>
  );
}

interface Props {
  index: number;
  streamId: string;
}

function Item({ index, streamId }: Props) {
  const { isDuration } = useTimeFlip(streamId);
  const { purpose } = useAdditionalFieldsPurpose(streamId);

  return (
    <Wrapper
      extra={<Extra streamId={streamId} />}
      title={`Stream #${index + 1}`}
      cy={`${tags.streamCard}-${streamId}`}
    >
      <Grid>
        <Fields.Amount streamId={streamId} />
        <Fields.Address streamId={streamId} />
        {isDuration ? (
          <Fields.Duration streamId={streamId} />
        ) : (
          <Moment>
            <Fields.Start streamId={streamId} />
            <Fields.End streamId={streamId} />
            <Fields.Switch streamId={streamId} />
          </Moment>
        )}
        <Extended>
          <Extension purpose={purpose} streamId={streamId} />
        </Extended>
      </Grid>
    </Wrapper>
  );
}

export default memo(Item);
